// Разбор markdown для вида «документ» и обратная сборка после правки на месте.
//
// Почему свой разбор, а не готовая библиотека. Редактирование в базе намеренно
// бедное (HUB_REQUIREMENTS §2, принцип 3): заголовки, жирный, курсив, списки,
// цитата, ссылка, код, таблица — и ничего больше. Полноценный markdown-движок
// тянет в бандл 50–100 КБ, а главную задачу всё равно не решает: вернуть
// отредактированный DOM обратно в markdown, не переписав остальной файл.
// Поэтому здесь ровно нужное подмножество и точная обратная сборка по кускам
// исходника.
//
// Ключевая идея всего файла — блок помнит СВОЙ кусок исходника (`src`) и
// пробел до следующего блока (`gap`). Тело собирается как
// `prefix + Σ(src + gap)` и байт в байт совпадает с исходным, пока никто ничего
// не правил. Правка одного абзаца меняет ровно один `src`, а не весь файл:
// иначе первое же автосохранение переписывало бы разметку, которую агент писал
// на диск руками, и лента изменений заполнилась бы ложными правками.

import type { TreeNode } from '../protocol'

// ---------- строчная разметка ----------

/** Кусок строки. `src` — исходный markdown куска, он же уезжает обратно в файл. */
export type Inline =
  | { kind: 'text'; text: string; src: string }
  | { kind: 'strong'; text: string; src: string }
  | { kind: 'em'; text: string; src: string }
  | { kind: 'code'; text: string; src: string }
  | { kind: 'wiki'; name: string; label: string; src: string }
  | { kind: 'link'; text: string; href: string; src: string }

// Порядок веток важен: код разбирается первым, иначе `**` внутри `код` считался
// бы жирным; wiki-ссылка — раньше обычной, иначе `[[имя]]` распалось бы на
// «[» + «[имя]» + «]». Переносы строк внутрь разметки не пускаем — незакрытая
// звёздочка иначе съедала бы полдокумента.
const INLINE_RE = new RegExp([
  '(`+)([^`\\n]+?)\\1',                                  // 1,2 — встроенный код
  '\\[\\[([^\\[\\]\\n|]+)(?:\\|([^\\[\\]\\n]*))?\\]\\]', // 3,4 — wiki-ссылка
  '\\*\\*([^\\n]+?)\\*\\*',                              // 5 — жирный
  '__([^\\n]+?)__',                                      // 6 — жирный
  '\\*([^*\\n]+?)\\*',                                   // 7 — курсив
  '_([^_\\n]+?)_',                                       // 8 — курсив
  '\\[([^\\]\\n]*)\\]\\(([^)\\s]+)\\)',                  // 9,10 — обычная ссылка
].join('|'), 'g')

/** Разобрать строку на куски. Неразмеченный текст остаётся текстом как есть. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  let last = 0
  INLINE_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = INLINE_RE.exec(text))) {
    if (m.index > last) out.push({ kind: 'text', text: text.slice(last, m.index), src: text.slice(last, m.index) })
    const src = m[0]
    if (m[2] !== undefined) out.push({ kind: 'code', text: m[2], src })
    else if (m[3] !== undefined) out.push({ kind: 'wiki', name: m[3].trim(), label: (m[4] ?? m[3]).trim(), src })
    else if (m[5] !== undefined) out.push({ kind: 'strong', text: m[5], src })
    else if (m[6] !== undefined) out.push({ kind: 'strong', text: m[6], src })
    else if (m[7] !== undefined) out.push({ kind: 'em', text: m[7], src })
    else if (m[8] !== undefined) out.push({ kind: 'em', text: m[8], src })
    else out.push({ kind: 'link', text: m[9] ?? '', href: m[10] ?? '', src })
    last = m.index + src.length
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last), src: text.slice(last) })
  return out
}

// ---------- блоки ----------

interface Span {
  /** исходный markdown блока, без пробельного хвоста */
  src: string
  /** пробелы и пустые строки до следующего блока — их и возвращаем в файл */
  gap: string
}

/** Разбор блока без привязки к исходнику — с ним же приходит правка. */
export type BlockBody =
  | { kind: 'heading'; level: number; text: string; anchor: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; start: number; marker: string; items: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'code'; lang: string; fence: string; text: string }
  | { kind: 'table'; head: string[]; rows: string[][] }
  | { kind: 'rule' }

export type Block = Span & BlockBody

export interface Heading { index: number; level: number; text: string; anchor: string }

export interface Doc {
  /** пустые строки перед первым блоком — сохраняем, чтобы не дёргать файл */
  prefix: string
  blocks: Block[]
  headings: Heading[]
}

const RE_FENCE = /^\s{0,3}(```|~~~)(.*)$/
const RE_HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/
const RE_RULE = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/
const RE_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/
const RE_QUOTE = /^\s{0,3}>\s?(.*)$/
const RE_TABLE = /^\s{0,3}\|/
const RE_TABLE_SEP = /^\s{0,3}\|?[\s:|-]*-[\s:|-]*\|?\s*$/

/** Начинается ли с этой строки блок, который нельзя приклеить к абзацу. */
function breaksParagraph(line: string, next: string | undefined): boolean {
  if (!line.trim()) return true
  if (RE_FENCE.test(line) || RE_HEADING.test(line) || RE_RULE.test(line)) return true
  if (RE_ITEM.test(line) || RE_QUOTE.test(line)) return true
  if (RE_TABLE.test(line) && next !== undefined && RE_TABLE_SEP.test(next)) return true
  return false
}

function splitRow(line: string): string[] {
  const inner = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return inner.split('|').map((c) => c.trim())
}

/** Разобрать тело заметки на блоки, сохранив исходник каждого куска. */
export function parseDoc(raw: string): Doc {
  const body = raw.replace(/\r\n?/g, '\n')
  const lines = body.split('\n')
  // Смещения строк в исходной строке — по ним `src` и `gap` вырезаются точно,
  // без догадок о том, сколько там было переводов строки.
  const starts: number[] = []
  const ends: number[] = []
  let off = 0
  for (const line of lines) {
    starts.push(off)
    ends.push(off + line.length)
    off += line.length + 1
  }

  const blocks: Block[] = []
  const spans: Array<[number, number]> = []
  const push = (block: BlockBody, a: number, b: number): void => {
    blocks.push({ ...block, src: body.slice(starts[a], ends[b - 1]), gap: '' })
    spans.push([a, b])
  }

  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (!line.trim()) { i++; continue }

    const fence = RE_FENCE.exec(line)
    if (fence) {
      let j = i + 1
      while (j < lines.length && !lines[j].trim().startsWith(fence[1])) j++
      const end = Math.min(lines.length, j + 1)
      push({ kind: 'code', lang: fence[2].trim(), fence: fence[1], text: lines.slice(i + 1, j).join('\n') }, i, end)
      i = end
      continue
    }

    const head = RE_HEADING.exec(line)
    if (head) {
      push({ kind: 'heading', level: head[1].length, text: head[2], anchor: `h-${blocks.length}` }, i, i + 1)
      i += 1
      continue
    }

    if (RE_RULE.test(line)) { push({ kind: 'rule' }, i, i + 1); i += 1; continue }

    if (RE_TABLE.test(line) && RE_TABLE_SEP.test(lines[i + 1] ?? '')) {
      let j = i + 2
      while (j < lines.length && RE_TABLE.test(lines[j])) j++
      const rows = lines.slice(i + 2, j).map(splitRow)
      push({ kind: 'table', head: splitRow(line), rows }, i, j)
      i = j
      continue
    }

    const item = RE_ITEM.exec(line)
    if (item) {
      const ordered = /\d/.test(item[2])
      const items: string[] = []
      let j = i
      while (j < lines.length) {
        const cur = RE_ITEM.exec(lines[j])
        if (cur) { items.push(cur[3]); j++; continue }
        // Продолжение пункта: непустая строка с отступом. Приклеиваем через
        // пробел — в markdown мягкий перенос внутри пункта и есть пробел.
        if (items.length && lines[j].trim() && /^\s+\S/.test(lines[j])) {
          items[items.length - 1] += ` ${lines[j].trim()}`
          j++
          continue
        }
        break
      }
      const start = ordered ? Number.parseInt(item[2], 10) || 1 : 1
      push({ kind: 'list', ordered, start, marker: item[2], items }, i, j)
      i = j
      continue
    }

    if (RE_QUOTE.test(line)) {
      const rows: string[] = []
      let j = i
      while (j < lines.length) {
        const q = RE_QUOTE.exec(lines[j])
        if (!q) break
        rows.push(q[1])
        j++
      }
      push({ kind: 'quote', lines: rows }, i, j)
      i = j
      continue
    }

    let j = i + 1
    while (j < lines.length && !breaksParagraph(lines[j], lines[j + 1])) j++
    // Мягкие переносы внутри абзаца — это пробелы: так markdown их и понимает,
    // и так они попадают в DOM. Значит, любой перенос строки, найденный потом
    // в DOM, оставил там оператор, а не исходник (см. serializeBlock).
    push({ kind: 'paragraph', text: lines.slice(i, j).map((l) => l.trim()).join(' ') }, i, j)
    i = j
  }

  for (let k = 0; k < blocks.length; k++) {
    const stop = ends[spans[k][1] - 1]
    const next = k + 1 < blocks.length ? starts[spans[k + 1][0]] : body.length
    blocks[k].gap = body.slice(stop, next)
  }

  const headings: Heading[] = []
  blocks.forEach((b, index) => {
    if (b.kind === 'heading') headings.push({ index, level: b.level, text: b.text, anchor: b.anchor })
  })

  return { prefix: blocks.length ? body.slice(0, starts[spans[0][0]]) : body, blocks, headings }
}

/** Собрать тело обратно: заменённые куски из `srcs`, остальное — как было. */
export function composeBody(doc: Doc, srcs: string[]): string {
  let out = doc.prefix
  doc.blocks.forEach((b, i) => { out += (srcs[i] ?? b.src) + b.gap })
  return out
}

// ---------- DOM → markdown ----------

/** Теги, которые в contentEditable означают перевод строки, а не разметку. */
const BLOCKISH = new Set(['DIV', 'P', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'])

/** Собрать markdown из отредактированного узла.
 *
 *  Каждый размеченный кусок нарисован span'ом с `data-md` (исходник) и
 *  `data-text` (то, что видно). Пока текст куска не тронут, возвращаем исходник —
 *  так жирный остаётся жирным, а wiki-ссылка ссылкой. Как только оператор
 *  правит текст внутри куска, разметка этого куска теряется и он становится
 *  обычным текстом: для намеренно бедного редактора это честнее, чем угадывать,
 *  где теперь заканчивались звёздочки.
 */
function fromDom(el: HTMLElement, br: string): string {
  let out = ''
  const walk = (node: Node): void => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) { out += (child as Text).data; return }
      const tag = (child as HTMLElement).tagName
      if (!tag) return
      const kid = child as HTMLElement
      if (tag === 'BR') { out += br; return }
      const md = kid.dataset?.md
      if (md !== undefined && kid.textContent === (kid.dataset.text ?? '')) { out += md; return }
      if (BLOCKISH.has(tag) && out && !out.endsWith(br)) out += br
      walk(kid)
    })
  }
  walk(el)
  // Браузер щедро сыплет неразрывными пробелами в contentEditable — в файле они
  // выглядят как мусор и ломают сравнение «текст не изменился».
  return out.replace(/\u00a0/g, ' ').replace(/[ \t]+$/gm, '').trim()
}

/** Превратить отредактированный блок обратно в markdown. */
export function serializeBlock(block: Block, el: HTMLElement): string {
  switch (block.kind) {
    case 'heading':
      return `${'#'.repeat(block.level)} ${fromDom(el, ' ')}`
    case 'paragraph':
      // Enter внутри абзаца — это новый абзац, поэтому перенос уезжает пустой
      // строкой. Мягких переносов в DOM изначально нет (см. parseDoc), так что
      // всё, что здесь найдено, оператор ввёл сам и ждёт именно абзаца.
      return fromDom(el, '\n\n')
    case 'list': {
      const items: string[] = []
      el.querySelectorAll(':scope > li').forEach((li) => {
        fromDom(li as HTMLElement, '\n').split('\n').forEach((row) => items.push(row))
      })
      const rows = items.filter((row) => row.trim())
      return rows
        .map((row, idx) => (block.ordered ? `${block.start + idx}. ${row}` : `${block.marker} ${row}`))
        .join('\n')
    }
    case 'quote':
      return fromDom(el, '\n').split('\n').map((row) => (row ? `> ${row}` : '>')).join('\n')
    case 'code': {
      const text = (el.textContent ?? '').replace(/\n$/, '')
      return `${block.fence}${block.lang}\n${text}\n${block.fence}`
    }
    default:
      // Таблица и разделитель на месте не правятся: собирать markdown-таблицу
      // из ячеек — это уже конструктор, которого в требованиях нет.
      return block.src
  }
}

// ---------- wiki-ссылки ----------

/** Ключ сравнения имён: регистр, ё/е и разделители значения не имеют. */
export function normalizeName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\.md$/, '')
    .replace(/[\s_-]+/g, ' ')
}

/** Имя → путь заметки. Строится из дерева (там имя = title) и из путей,
 *  которые сервер уже разрешил в поле `links`. */
export function buildLinkIndex(tree: TreeNode[], links: string[] = []): Map<string, string> {
  const index = new Map<string, string>()
  const add = (key: string, path: string): void => {
    const k = normalizeName(key)
    if (k && !index.has(k)) index.set(k, path)
  }
  const walk = (nodes: TreeNode[]): void => {
    for (const node of nodes) {
      if (node.kind === 'note') {
        add(node.name, node.path)
        add(node.path.split('/').pop() ?? node.path, node.path)
        add(node.path, node.path)
      }
      if (node.children?.length) walk(node.children)
    }
  }
  walk(tree)
  for (const path of links) {
    add(path.split('/').pop() ?? path, path)
    add(path, path)
  }
  return index
}

export interface WikiTarget {
  /** путь заметки; null — путь определить не удалось */
  path: string | null
  /** заметки с таким именем ещё нет (поле `missing` из ответа) */
  missing: boolean
}

export function resolveWiki(name: string, index: Map<string, string>, missing: Set<string>): WikiTarget {
  const key = normalizeName(name)
  const path = index.get(key) ?? null
  if (path) return { path, missing: false }
  return { path: null, missing: missing.has(key) }
}

// ---------- права ----------

/** Память агента — только для чтения (FR-11, FR-28).
 *
 *  Признак берём по первому сегменту пути: отдельного флага в API нет, а
 *  последнее слово всё равно за сервером — он отвечает на PUT ошибкой, и мы её
 *  показываем, не теряя набранного. Эта проверка лишь избавляет от бессмысленного
 *  набора текста в файл, который заведомо не сохранится.
 */
export function isReadOnlyPath(path: string): boolean {
  const first = normalizeName(path.split('/')[0] ?? '')
  return first === 'память' || first === 'memory' || first === 'память агента'
}
